import React, { useEffect, useMemo, useState, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

import { getAccountById, getAccountPatients } from "../api";
import { EditAccount } from "../Components/PatientsAccounts/EditAccounts";
import { NotesHistory } from "../Components/PatientsAccounts/AccountNotes";
import AccountStatements from "../Components/Accounts/AccountStatements";
import { Breadcrumb } from '../Components/Breadcrumb/Breadcrumb';

export function Accounts() {
  const { id } = useParams();
  const location = useLocation();

  const [account, setAccount] = useState(null);
  const [dependants, setDependants] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [activeSection, setActiveSection] = useState("account");
  const navigate = useNavigate();

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toISOString().slice(0, 16).replace("T", " ");
  }, []);

  const hasMedicalAid = useMemo(() => {
    return !!(account?.accountMedicalAid && String(account.accountMedicalAid).trim());
  }, [account]);

  const hasIOD = useMemo(() => {
    return !!(account?.accountIODFund && String(account.accountIODFund).trim());
  }, [account]);

  const canAccessPatients = useMemo(() => {
    try {
      const token = sessionStorage.getItem('user');
      if (!token) return false;
      const user = jwtDecode(token);
      const roles = [
        ...(Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : []),
        ...(Array.isArray(user?.permissions) ? user.permissions : []),
      ];
      return roles.some(r => ['SuperAdmin', 'Receptionist'].includes(r));
    } catch { return false; }
  }, []);

  const canAccessAccounts = useMemo(() => {
    try {
      const token = sessionStorage.getItem('user');
      if (!token) return false;
      const user = jwtDecode(token);
      const roles = [
        ...(Array.isArray(user?.role) ? user.role : user?.role ? [user.role] : []),
        ...(Array.isArray(user?.permissions) ? user.permissions : []),
      ];
      return roles.some(r => ['SuperAdmin', 'Finance'].includes(r));
    } catch { return false; }
  }, []);

  const breadcrumbItems = useMemo(() => {
    if (!account) return null;

    const fi = (account.accountFullNames || '').charAt(0);
    const accountLabel = `Account - ${fi ? fi + ' ' : ''}${account.accountLastName || ''}`.trim() || 'Account';
    const accountCrumb = { label: accountLabel, path: activeSection !== 'account' ? `/accounts/${id}` : undefined };

    let base;

    if (location.state?.fromAppointment) {
      // Appointment > [Date - Name] > [Account Name]
      base = [
        { label: 'Appointments', path: '/bookings' },
        { label: location.state.appointmentLabel, path: location.state.appointmentPath },
        accountCrumb,
      ];
    } else if (location.state?.fromDashboard) {
      // Dashboard > [Patient Name] > [Account Name]
      base = [
        { label: 'Dashboard', path: '/home' },
        { label: location.state.patientName, path: `/patients/appointment/${location.state.patientId}/1`, canAccess: canAccessPatients },
        accountCrumb,
      ];
    } else if (location.state?.fromPatient) {
      // [Patient Name] > [Account Name]  (no broken /patients link)
      base = [
        { label: location.state.patientName, path: `/patients/appointment/${location.state.patientId}/1`, canAccess: canAccessPatients },
        accountCrumb,
      ];
    } else if (location.state?.breadcrumbTrail?.length) {
      // Generic upstream trail (e.g. from InvoiceList)
      base = [...location.state.breadcrumbTrail, accountCrumb];
    } else {
      // Accounts > [Account Name]
      base = [
        { label: 'Accounts', path: '/billing', canAccess: canAccessAccounts },
        accountCrumb,
      ];
    }

    return base;
  }, [account, activeSection, id, location.state, canAccessPatients, canAccessAccounts]);

  // ✅ Consistent icon spacing everywhere
  const IconLine = ({ icon, children, className = "" }) => (
    <div className={`d-flex align-items-start mb-1 ${className}`}>
      <i className={`${icon} text-primary me-2 mt-1`} />
      <div className="lh-sm">{children}</div>
    </div>
  );

  const getStatusClass = (status) =>
    status?.toLowerCase().includes("active") ? "text-success" : "text-danger";

  // ✅ Load account
  useEffect(() => {
    async function getAccountInfo() {
      try {
        const accountData = await getAccountById(id);
        setAccount(accountData);
        console.log("📥 Account fetched:", accountData);
      } catch (error) {
        console.error("Error fetching account data:", error);
      }
    }
    getAccountInfo();
  }, [id]);

  // ✅ Load dependants (patients on account)
  useEffect(() => {
    async function loadDependants() {
      try {
        if (!account?._id) return;
        const dependantsData = await getAccountPatients(id);
        setDependants(Array.isArray(dependantsData) ? dependantsData : []);
        console.log("📥 Dependants fetched:", dependantsData);
      } catch (error) {
        console.error("Error fetching dependants data:", error);
        setDependants([]);
      }
    }
    loadDependants();
  }, [id, account?._id]);

  function renderAccountInfo() {
    if (!account?._id) return <p>Loading account information...</p>;

    return (
      <div>
        <Row className="align-items-center mb-3">
          <Col md={10}>
            <Row className="mb-2">
              <Col md={12}>
                <h6 className="text-primary">
                  {account?.accountTitle} {account?.accountFullNames}{" "}
                  {account?.accountLastName} ({account?.accountFirstName})
                </h6>
              </Col>
            </Row>

            <Row>
              {/* Column 1 */}
              <Col md={4}>
                {account?.accountIdNumber && (
                  <IconLine icon="fa-solid fa-id-card">
                    {account.accountIdNumber}
                  </IconLine>
                )}
                {account?.accountTelNumberCell && (
                  <IconLine icon="fas fa-phone-alt">
                    {account.accountTelNumberCell}
                  </IconLine>
                )}
                {account?.accountEmail && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail}</IconLine>
                )}
                {account?.accountEmail2 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail2}</IconLine>
                )}
                {account?.accountEmail3 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail3}</IconLine>
                )}
                {account?.accountEmail4 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail4}</IconLine>
                )}
                {account?.accountEmail5 && (
                  <IconLine icon="fas fa-envelope">{account.accountEmail5}</IconLine>
                )}
              </Col>

              {/* Column 2 */}
              <Col md={4}>
                {(account?.accountHomeAddress1 ||
                  account?.accountHomeAddress2 ||
                  account?.accountHomeAddressCity ||
                  account?.accountHomeAddressPostalCode) && (
                  <>
                    {account?.accountHomeAddress1 && (
                      <IconLine icon="fas fa-map-marker-alt">
                        {account.accountHomeAddress1}
                      </IconLine>
                    )}
                    {account?.accountHomeAddress2 && (
                      <div className="ps-4 text-muted small mb-1">
                        {account.accountHomeAddress2}
                      </div>
                    )}
                    {(account?.accountHomeAddressCity ||
                      account?.accountHomeAddressPostalCode) && (
                      <div className="ps-4 text-muted small">
                        {account?.accountHomeAddressCity || ""}
                        {account?.accountHomeAddressCity &&
                        account?.accountHomeAddressPostalCode
                          ? ", "
                          : ""}
                        {account?.accountHomeAddressPostalCode || ""}
                      </div>
                    )}
                  </>
                )}
              </Col>

              {/* Column 3 */}
              <Col md={4}>
                {account?.accountOccupation && (
                  <IconLine icon="fas fa-briefcase">{account.accountOccupation}</IconLine>
                )}
                {account?.accountEmployer && (
                  <IconLine icon="fas fa-building">{account.accountEmployer}</IconLine>
                )}
                {account?.accountTelNumberWork && (
                  <IconLine icon="fas fa-phone-alt">{account.accountTelNumberWork}</IconLine>
                )}
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={7} className="d-flex justify-content-start gap-2 w-25">
            <EditAccount account={account} show={modalShow} />
            <NotesHistory show={modalShow} account={account} />
          </Col>
        </Row>
      </div>
    );
  }

  function renderCoverInformation() {
    if (!account?._id) return <p>Loading account information...</p>;

    // ✅ Title rules you asked for
    const sectionTitle = hasIOD
      ? "Injury on Duty Information"
      : hasMedicalAid
      ? "Medical Aid Information"
      : "Private Account Information";

    // ✅ Sort dependants numerically by dependant code (only meaningful for medical aid)
    const sortedDependants = [...dependants].sort((a, b) => {
      const aCode = parseInt(a.patientMedicalAidDependantCode || "0", 10);
      const bCode = parseInt(b.patientMedicalAidDependantCode || "0", 10);
      return aCode - bCode;
    });

    return (
      <div>
        <Row className="mb-1">
          <Col md={10}>
            <Row>
              {/* LEFT: Cover info */}
              <Col md={4}>
                <h6 className="text-primary">{sectionTitle}</h6>

                {/* ✅ IOD */}
                {hasIOD && (
                  <>
                    <IconLine icon="fa-solid fa-briefcase-medical">
                      <strong>IOD Fund:</strong> {account.accountIODFund}
                    </IconLine>

                    {(account.accountIODPlan || account.accountIODOption) && (
                      <div className="ps-4 text-muted small mb-1">
                        {account.accountIODPlan || "-"}
                        {account.accountIODPlan && account.accountIODOption ? " | " : ""}
                        {account.accountIODOption || ""}
                      </div>
                    )}

                    {account.accountIODClaimRefNumber && (
                      <IconLine icon="fa-solid fa-hashtag">
                        <strong>Claim Ref #:</strong> {account.accountIODClaimRefNumber}
                      </IconLine>
                    )}

                    {account.accountIODEmployer && (
                      <IconLine icon="fa-solid fa-building">
                        <strong>Employer:</strong> {account.accountIODEmployer}
                      </IconLine>
                    )}

                    {account.accountIODEmployerWCARegNum && (
                      <IconLine icon="fa-solid fa-id-card-clip">
                        <strong>Employer WCA Reg #:</strong>{" "}
                        {account.accountIODEmployerWCARegNum}
                      </IconLine>
                    )}

                    {account.accountIODDateOfInjury && (
                      <IconLine icon="fa-solid fa-calendar-days">
                        <strong>Date of Injury:</strong> {account.accountIODDateOfInjury}
                      </IconLine>
                    )}
                  </>
                )}

                {/* ✅ Medical Aid */}
                {!hasIOD && hasMedicalAid && (
                  <>
                    <IconLine icon="fa fa-notes-medical">
                      <strong>Medical Aid:</strong> {account.accountMedicalAid}
                    </IconLine>

                    {(account.accountMedicalAidPlan || account.accountMedicalAidOption) && (
                      <div className="ps-4 text-muted small mb-1">
                        {account.accountMedicalAidPlan || "-"}
                        {account.accountMedicalAidPlan && account.accountMedicalAidOption
                          ? " | "
                          : ""}
                        {account.accountMedicalAidOption || ""}
                      </div>
                    )}

                    {account.accountMedicalAidNumber && (
                      <IconLine icon="fa-solid fa-hashtag">
                        <strong>Number:</strong> {account.accountMedicalAidNumber}
                      </IconLine>
                    )}
                  </>
                )}

                {/* ✅ Private */}
                {!hasIOD && !hasMedicalAid && (
                  <div className="text-muted small">
                    No Medical Aid or IOD cover captured for this account.
                  </div>
                )}
              </Col>

              {/* RIGHT: Dependants (only meaningful when Medical Aid exists) */}
              <Col md={8}>
                <h6 className="text-primary">Dependants</h6>

                {sortedDependants.length > 0 ? (
                  <Row as="ul" className="list-unstyled">
                    {sortedDependants.map((p) => (
                      <Row
                        as="li"
                        key={p?._id}
                        className="d-flex align-items-start mb-1"
                      >
                        <Col md={4}>
                          <div
                            className="text-primary"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              // Build trail: upstream ancestors + account (always with path)
                              const fi = (account.accountFullNames || '').charAt(0);
                              const acctLabel = `Account - ${fi ? fi + ' ' : ''}${account.accountLastName || ''}`.trim() || 'Account';
                              const ancestors = breadcrumbItems ? breadcrumbItems.slice(0, -1) : [];
                              const trail = [...ancestors, { label: acctLabel, path: `/accounts/${id}` }];
                              navigate(`/patients/appointment/${p._id}/1`, { state: { breadcrumbTrail: trail } });
                            }}
                          >
                            {p?.patientMedicalAidDependantCode
                              ? `${p.patientMedicalAidDependantCode} - `
                              : ""}
                            {p?.patientFirstName} {p?.patientLastName}
                          </div>
                        </Col>

                        {/* ✅ Only show these when Medical Aid exists on the account */}
                        {hasMedicalAid && (
                          <>
                            <Col md={4}>
                              <div className={getStatusClass(p?.patientMedicalAidStatus)}>
                                {p?.patientMedicalAidStatus || ""}
                                {p?.patientMedicalAidJoinDate
                                  ? ` ${p.patientMedicalAidJoinDate}`
                                  : ""}
                              </div>
                            </Col>

                            <Col md={4} className="text-start">
                              <div className="text-muted">
                                checked: {formatDate(p?.patientMedicalAidLastCheck)}
                              </div>
                            </Col>
                          </>
                        )}
                      </Row>
                    ))}
                  </Row>
                ) : (
                  <p className="text-muted mb-0">No associated patients found.</p>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginTop: "65px", position: "relative" }} className="ms-3">
        {breadcrumbItems && <Breadcrumb items={breadcrumbItems} />}
        <div
          className="container-fluid px-4 px-md-1 bg-white pt-2"
          style={{ maxWidth: "1400px" }}
        >
          {/* Tabs */}
          <Row className="d-flex justify-content-between align-items-center mb-2">
            <Col md={10}>
              <div className="d-flex align-items-center">
                <h5
                  className={`fw-normal d-inline me-3 ${
                    activeSection === "account"
                      ? "text-primary"
                      : "text-secondary fw-light"
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setActiveSection("account")}
                >
                  Account Information
                </h5>
                <span className="text-secondary mx-3">|</span>
                <h5
                  className={`fw-normal d-inline ${
                    activeSection === "medicalAid"
                      ? "text-primary"
                      : "text-secondary fw-light"
                  }`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setActiveSection("medicalAid")}
                >
                  {hasIOD
                    ? "Injury on Duty Information"
                    : hasMedicalAid
                    ? "Medical Aid Information"
                    : "Private Account Information"}
                </h5>
              </div>
            </Col>
          </Row>

          {/* Content */}
          <Row className="ms-1 mb-1">
            {activeSection === "account" && renderAccountInfo()}
            {activeSection === "medicalAid" && renderCoverInformation()}
          </Row>
        </div>
      </div>

      <hr />

      <Row className="ms-1">
        <AccountStatements account={account} breadcrumbContext={location.state} />
      </Row>
    </div>
  );
}
